import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScheduleDelivery = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedDate, setConfirmedDate] = useState('');
  const [isReschedule, setIsReschedule] = useState(false);
  const [errorKind, setErrorKind] = useState(null); // 'expired' | 'not_found' | 'other'

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await axios.get(`${API}/schedule/${token}`);
        setData(res.data);
        if (res.data.booked && !res.data.available_dates?.length) {
          setConfirmed(true);
          setConfirmedDate(res.data.booked_date);
        } else if (res.data.booked) {
          setIsReschedule(true);
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 410) {
          setErrorKind('expired');
          setError('This scheduling link has expired.');
        } else if (status === 404) {
          setErrorKind('not_found');
          setError('We couldn\'t find this scheduling link.');
        } else {
          setErrorKind('other');
          setError(err.response?.data?.detail || 'This scheduling link is invalid or has expired.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleBook = async () => {
    if (!selectedDate) return;
    setBooking(true);
    try {
      const res = await axios.post(`${API}/schedule/${token}/book`, { date: selectedDate });
      setConfirmed(true);
      setConfirmedDate(selectedDate);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to schedule delivery.');
    } finally {
      setBooking(false);
    }
  };

  const handleReschedule = () => {
    setConfirmed(false);
    setIsReschedule(true);
    setSelectedDate(null);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return {
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      day: date.getDate(),
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
      </div>
    );
  }

  // Shared background wrapper
  const PageWrapper = ({ children }) => (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/delivery-bg.jpg)' }}
      />
      <div className="absolute inset-0 bg-black/40" />
      {/* Content */}
      <div className="relative z-10 w-full max-w-lg">
        {children}
      </div>
    </div>
  );

  if (error) {
    return (
      <PageWrapper>
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 text-center" data-testid="schedule-error">
          <Calendar className="h-12 w-12 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-700 text-lg font-medium mb-3" data-testid="schedule-error-message">{error}</p>
          {errorKind === 'expired' && (
            <p className="text-sm text-stone-500 mt-2">
              Please reply to the original email and ask the sender to issue a fresh scheduling link.
            </p>
          )}
          {errorKind === 'not_found' && (
            <p className="text-sm text-stone-500 mt-2">
              The link may be malformed. If you copied it from an email, try clicking the link directly instead.
            </p>
          )}
        </div>
      </PageWrapper>
    );
  }

  if (confirmed) {
    return (
      <PageWrapper>
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 text-center" data-testid="schedule-booked">
          {data?.logo_url && (
            <img src={data.logo_url} alt={data.company_name} className="h-32 mx-auto mb-6 object-contain" />
          )}
          <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="font-bold text-2xl text-stone-900 mb-2">
            {isReschedule ? 'Date Updated!' : 'You\'re All Set!'}
          </h2>
          <p className="text-stone-600 mb-6">
            Your complimentary box of assorted donuts will be delivered on:
          </p>
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 mb-6 shadow-lg">
            <p className="text-amber-100 text-xs uppercase tracking-widest mb-1">Delivery Date</p>
            <p className="text-white font-bold text-xl">{formatDate(confirmedDate)}</p>
          </div>
          <p className="text-stone-500 text-sm mb-6">
            A confirmation email has been sent. We can't wait to treat you!
          </p>
          {data?.available_dates?.length > 0 && (
            <button
              onClick={handleReschedule}
              className="text-sm text-amber-700 hover:text-amber-800 underline underline-offset-2"
              data-testid="reschedule-btn"
            >
              Need to change the date?
            </button>
          )}
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden" data-testid="schedule-page">
        {/* Header */}
        <div className="p-8 pb-4 text-center">
          {data?.logo_url && (
            <img src={data.logo_url} alt={data.company_name} className="h-32 mx-auto mb-5 object-contain" />
          )}
          <h1 className="font-bold text-2xl text-stone-900 mb-1">
            {data?.company_name || 'Complimentary Delivery'}
          </h1>
          <p className="text-stone-500">
            {isReschedule
              ? 'Pick a new delivery date for your complimentary box'
              : 'Pick a delivery date for your complimentary box of assorted donuts'}
          </p>
        </div>

        {/* Date grid */}
        <div className="px-8 pb-8">
          {data?.available_dates?.length > 0 ? (
            <div className="space-y-5">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="date-grid">
                {data.available_dates.map((dateStr) => {
                  const d = formatShortDate(dateStr);
                  const isSelected = selectedDate === dateStr;
                  return (
                    <button
                      key={dateStr}
                      onClick={() => setSelectedDate(dateStr)}
                      className={`p-3.5 rounded-xl border-2 text-center transition-all duration-150 ${
                        isSelected
                          ? 'border-amber-500 bg-amber-50 shadow-md shadow-amber-100'
                          : 'border-stone-200 hover:border-amber-300 hover:bg-stone-50'
                      }`}
                      data-testid={`date-${dateStr}`}
                    >
                      <p className={`text-xs font-semibold uppercase tracking-wide ${isSelected ? 'text-amber-600' : 'text-stone-400'}`}>
                        {d.weekday}
                      </p>
                      <p className={`text-xl font-bold mt-0.5 ${isSelected ? 'text-amber-700' : 'text-stone-800'}`}>
                        {d.day}
                      </p>
                      <p className={`text-xs mt-0.5 ${isSelected ? 'text-amber-600' : 'text-stone-400'}`}>
                        {d.month}
                      </p>
                    </button>
                  );
                })}
              </div>

              {selectedDate && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-center">
                  <p className="text-sm text-amber-800">
                    Selected: <strong>{formatDate(selectedDate)}</strong>
                  </p>
                </div>
              )}

              <Button
                onClick={handleBook}
                disabled={!selectedDate || booking}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-base rounded-xl shadow-lg shadow-amber-200/50 transition-all"
                data-testid="confirm-delivery-btn"
              >
                {booking ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Scheduling...</>
                ) : (
                  <><Calendar className="mr-2 h-5 w-5" />{isReschedule ? 'Confirm New Date' : 'Confirm Delivery Date'}</>
                )}
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No delivery dates available right now. Please check back soon.</p>
            </div>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default ScheduleDelivery;
